import React, { useCallback, useMemo, useState } from "react";

import { StackedNavigationContext } from "./StackedNavigationContext";

interface IProviderProps {
  screenIds?: string[];
  children: React.ReactNode;
}

const StackedNavigationProvider: React.FC<IProviderProps> = ({ screenIds, children }) => {
  const [stack, setStack] = useState<string[]>([]);
  const [params, setParams] = useState<Record<string, unknown>>({});

  const open = useCallback(
    (entryId: string, entryParams?: Record<string, unknown>) => {
      // Only validate if screenIds provided (for early error detection)
      if (screenIds && !screenIds.includes(entryId)) {
        throw new Error(`Invalid screen ID: ${entryId}`);
      }

      setStack((currentStack) => [...currentStack, entryId]);
      if (entryParams) {
        setParams((currentParams) => ({ ...currentParams, [entryId]: entryParams }));
      }
    },
    [screenIds]
  );

  const popStack = useCallback((count = 1) => {
    setStack((currentStack) => currentStack.slice(0, currentStack.length - count));
  }, []);

  const resetStack = useCallback(() => {
    setStack([]);
  }, []);

  const providerValue = useMemo(() => ({ stack, params, open, goBack: popStack, clear: resetStack }), [open, stack, params, popStack, resetStack]);

  return <StackedNavigationContext.Provider value={providerValue}>{children}</StackedNavigationContext.Provider>;
};

export default StackedNavigationProvider;
